import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { podcastsApi } from "../api/podcastsApi";
import { useAuth } from "../auth/authStore";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useToast } from "../ui/toast/ToastProvider";
import { usePodcastPlayer } from "../podcasts/playerStore";
import { isAegisEmbedUrl, resolveAegisPlayerUrl } from "../podcasts/aegisPlayer";
import { getSiteOrigin, joinUrl } from "../utils/seo";
import { openExternalUrl } from "../utils/safeUrl";
import { buildPsychologistProfilePath } from "../utils/psychologistProfile";
import ReportContentButton from "../components/reports/ReportContentButton";

const PAGE_SIZE = 20;

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function safeText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("ru-RU").format(n);
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeText(value, "—");
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDurationFromSeconds(seconds) {
  const sec = Number(seconds);
  if (!Number.isFinite(sec) || sec <= 0) return "";
  const minutes = Math.round(sec / 60);
  return `${minutes} мин`;
}

function formatEpisodeCount(value) {
  const n = Number(value) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  let suffix = "эпизодов";
  if (mod10 === 1 && mod100 !== 11) suffix = "эпизод";
  else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) suffix = "эпизода";
  return `${formatNumber(n)} ${suffix}`;
}

function initials(name) {
  const parsed = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0])
    .join("");
  return parsed ? parsed.toUpperCase() : "BS";
}

function topicColorClass(topic, index = 0) {
  const t = String(topic || "").toLowerCase();
  if (t.includes("трев")) return "is-mint";
  if (t.includes("отнош")) return "is-lav";
  if (t.includes("карьер")) return "is-amber";
  if (t.includes("трав")) return "is-sky";
  if (t.includes("само")) return "is-coral";
  const byIndex = ["is-mint", "is-lav", "is-amber", "is-sky", "is-coral"];
  return byIndex[index % byIndex.length];
}

function parseDateValue(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getTime();
}

function extractPage(source) {
  if (Array.isArray(source)) {
    return {
      content: source,
      number: 0,
      size: source.length || PAGE_SIZE,
      totalElements: source.length,
      totalPages: 1,
    };
  }
  const page = asObject(source);
  const content = Array.isArray(page.content)
    ? page.content
    : Array.isArray(page.items)
      ? page.items
      : [];
  const size = Number(page.size) || content.length || PAGE_SIZE;
  const totalElements = Number(page.totalElements) || content.length;
  const totalPages = Number(page.totalPages) || Math.max(1, Math.ceil(totalElements / Math.max(size, 1)));
  return {
    content,
    number: Number(page.number) || 0,
    size,
    totalElements,
    totalPages,
  };
}

function normalizeEpisode(input = {}) {
  const raw = asObject(input);
  const author = asObject(raw.author);
  const publishedAt = raw.publishedAt || raw.publishDate || raw.date || raw.createdAt || null;
  const durationLabel =
    formatDurationFromSeconds(raw.durationSeconds) ||
    safeText(raw.durationLabel || raw.duration || raw.length, "");

  return {
    id: raw.id ?? null,
    podcastId: raw.podcastId ?? null,
    podcastSlug: safeText(raw.podcastSlug),
    podcastTitle: safeText(raw.podcastTitle || raw.showTitle || "Подкаст"),
    podcastCoverEmoji: safeText(raw.podcastCoverEmoji || "🎙️"),
    authorName: safeText(author.name || raw.authorName || "Специалист"),
    authorUserId: author.userId ?? author.id ?? raw.authorUserId ?? null,
    episodeNumber: Number(raw.episodeNumber) || 0,
    title: safeText(raw.title || "Эпизод"),
    description: safeText(raw.description || raw.shortDescription),
    durationLabel: durationLabel || "—",
    durationSeconds: Number(raw.durationSeconds) || 0,
    publishedAt,
    publishedLabel: formatDate(publishedAt),
    listensCount: Number(raw.listensCount || raw.totalListens || 0),
    saved: Boolean(raw.saved),
    playbackUrl: safeText(raw.playbackUrl),
    externalUrl: safeText(raw.externalUrl),
    aegisAssetId: safeText(raw.aegisAssetId || raw.assetId || raw.aegis_asset_id || raw.asset_id),
    aegisProjectId: safeText(raw.aegisProjectId || raw.projectId || raw.aegis_project_id || raw.project_id),
    aegisPlayerUrl: resolveAegisPlayerUrl(raw),
    isAegisEmbed:
      Boolean(resolveAegisPlayerUrl(raw)) ||
      isAegisEmbedUrl(raw.playbackUrl || "") ||
      isAegisEmbedUrl(raw.externalUrl || ""),
    coverImageUrl: safeText(raw.coverImageUrl || raw.imageUrl || raw.coverUrl || raw.podcastCoverUrl || raw.podcastCoverImageUrl),
    isNew: Boolean(raw.isNew || raw.featured),
  };
}

function normalizePodcastDetail(input = {}) {
  const root = asObject(input);
  const base = asObject(root.podcast || root.profile || root.detail || root);
  const author = asObject(base.author || root.author);

  const topics = asArray(base.topics || base.topicLabels || base.tags || root.topics || root.tags)
    .map((item) => (typeof item === "string" ? item : safeText(item?.title || item?.name || item?.label)))
    .filter(Boolean);

  const pageSource =
    root.episodesPage ||
    root.episodes ||
    root.items ||
    base.episodes ||
    base.latestEpisodes ||
    [];
  const episodesPage = extractPage(pageSource);
  const episodes = asArray(episodesPage.content).map(normalizeEpisode);

  const lastEpisode =
    normalizeEpisode(base.lastEpisode || root.lastEpisode || episodes[0] || {});

  return {
    id: base.id ?? root.id ?? null,
    slug: safeText(base.slug || root.slug),
    title: safeText(base.title || root.title || "Подкаст"),
    description: safeText(base.description || base.shortDescription || root.description),
    coverImageUrl: safeText(base.coverImageUrl || base.imageUrl || base.coverUrl || root.coverImageUrl),
    coverEmoji: safeText(base.coverEmoji || root.coverEmoji || "🎙️"),
    authorName: safeText(author.name || base.authorName || root.authorName || "Специалист"),
    authorAvatar: safeText(author.avatarUrl || base.authorAvatar || root.authorAvatar),
    authorUserId: author.userId ?? author.id ?? base.authorUserId ?? root.authorUserId ?? null,
    topics,
    subscribed: Boolean(base.subscribed ?? root.subscribed),
    totalListens: Number(base.totalListens || base.listensCount || root.totalListens || root.listensCount || 0),
    episodesCount: Number(base.episodesCount || root.episodesCount || episodesPage.totalElements || episodes.length || 0),
    episodes,
    page: episodesPage.number,
    size: episodesPage.size,
    totalElements: episodesPage.totalElements,
    totalPages: Math.max(1, episodesPage.totalPages),
    lastEpisode: lastEpisode.id ? lastEpisode : null,
  };
}

export default function PodcastDetails() {
  const { podcastId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, me, role } = useAuth();
  const player = usePodcastPlayer();
  const canManagePodcasts = ["PSYCHOLOGIST", "ADMIN", "MODERATOR"].includes(String(role || "").toUpperCase());

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState("new");
  const [page, setPage] = React.useState(0);
  const [detail, setDetail] = React.useState(() =>
    normalizePodcastDetail({
      title: "Подкаст",
      episodes: [],
    })
  );

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(
    () => joinUrl(origin, `/podcasts/${encodeURIComponent(String(podcastId || ""))}`),
    [origin, podcastId]
  );
  const openPsychologistProfile = React.useCallback(
    (event, userId) => {
      const path = buildPsychologistProfilePath(userId);
      if (!path) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      navigate(path);
    },
    [navigate]
  );

  React.useEffect(() => {
    setPage(0);
  }, [podcastId]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    podcastsApi
      .getPodcastProfile(podcastId, { page, size: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        const normalized = normalizePodcastDetail(res || {});
        setDetail(normalized);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Не удалось загрузить подкаст");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [podcastId, page]);

  const sortedEpisodes = React.useMemo(() => {
    const items = [...detail.episodes];
    items.sort((a, b) => {
      const aDate = parseDateValue(a.publishedAt);
      const bDate = parseDateValue(b.publishedAt);
      if (aDate !== null && bDate !== null) return sortOrder === "old" ? aDate - bDate : bDate - aDate;
      if (a.episodeNumber && b.episodeNumber) {
        return sortOrder === "old"
          ? a.episodeNumber - b.episodeNumber
          : b.episodeNumber - a.episodeNumber;
      }
      return 0;
    });
    return items;
  }, [detail.episodes, sortOrder]);

  const openEpisode = (episode) => {
    if (!episode) return;
    if (episode.id !== null && episode.id !== undefined) {
      navigate(`/podcasts/episodes/${encodeURIComponent(String(episode.id))}`);
      return;
    }
    const fallbackUrl = episode.externalUrl || episode.playbackUrl;
    if (fallbackUrl && !openExternalUrl(fallbackUrl)) {
      toast.error("Некорректная ссылка эпизода");
    }
  };

  const playEpisodeInline = async (episode) => {
    if (!episode) return;
    const playbackQueue = Array.isArray(sortedEpisodes) && sortedEpisodes.length ? sortedEpisodes : [episode];
    const queueStartIndex = playbackQueue.findIndex((item) => String(item?.id ?? "") === String(episode?.id ?? ""));
    const ok = await player.playTrack(
      {
        id: episode.id,
        episodeId: episode.id,
        podcastId: episode.podcastId || detail.id,
        podcastSlug: episode.podcastSlug || detail.slug,
        title: episode.title,
        podcastTitle: episode.podcastTitle || detail.title,
        coverImageUrl: episode.coverImageUrl || detail.coverImageUrl,
        durationSeconds: episode.durationSeconds,
        playbackUrl: episode.playbackUrl || episode.externalUrl,
        assetId: episode.aegisAssetId,
        projectId: episode.aegisProjectId,
        isAegisEmbed: episode.isAegisEmbed,
      },
      {
        autoplay: true,
        queue: playbackQueue,
        queueStartIndex: queueStartIndex >= 0 ? queueStartIndex : 0,
      }
    );
    if (!ok) {
      openEpisode(episode);
      return;
    }
    if (episode.id) {
      podcastsApi.registerListen(episode.id)
        .then((listen) => {
          const nextEpisodeListens = Number(listen?.episodeListens);
          const nextPodcastListens = Number(listen?.podcastListens);
          setDetail((prev) => ({
            ...prev,
            totalListens: Number.isFinite(nextPodcastListens)
              ? nextPodcastListens
              : prev.totalListens,
            episodes: prev.episodes.map((item) =>
              String(item.id) === String(episode.id) && Number.isFinite(nextEpisodeListens)
                ? { ...item, listensCount: nextEpisodeListens }
                : item
            ),
            lastEpisode:
              prev.lastEpisode && String(prev.lastEpisode.id) === String(episode.id) && Number.isFinite(nextEpisodeListens)
                ? { ...prev.lastEpisode, listensCount: nextEpisodeListens }
                : prev.lastEpisode,
          }));
        })
        .catch(() => {});
    }
  };

  const toggleSubscribe = async () => {
    if (!detail.id) return;
    if (!isAuthenticated) {
      toast.info("Подписка доступна после авторизации");
      navigate("/login");
      return;
    }
    try {
      if (detail.subscribed) await podcastsApi.unsubscribe(detail.id);
      else await podcastsApi.subscribe(detail.id);
      setDetail((prev) => ({ ...prev, subscribed: !prev.subscribed }));
      toast.success(detail.subscribed ? "Подписка отменена" : "Подписка оформлена");
    } catch (err) {
      toast.error(err?.message || "Не удалось изменить подписку");
    }
  };

  const toggleSaveEpisode = async (event, episode) => {
    event.stopPropagation();
    if (!episode?.id) return;
    if (!isAuthenticated) {
      toast.info("Сохранение доступно после авторизации");
      navigate("/login");
      return;
    }
    try {
      if (episode.saved) await podcastsApi.unsaveEpisode(episode.id);
      else await podcastsApi.saveEpisode(episode.id);
      setDetail((prev) => ({
        ...prev,
        episodes: prev.episodes.map((item) =>
          item.id === episode.id ? { ...item, saved: !item.saved } : item
        ),
        lastEpisode:
          prev.lastEpisode?.id === episode.id
            ? { ...prev.lastEpisode, saved: !prev.lastEpisode.saved }
            : prev.lastEpisode,
      }));
      toast.success(episode.saved ? "Убрано из сохранённого" : "Эпизод сохранён");
    } catch (err) {
      toast.error(err?.message || "Не удалось сохранить эпизод");
    }
  };

  const onSearchSubmit = (event) => {
    event.preventDefault();
    const q = safeText(searchInput);
    if (!q) {
      navigate("/podcasts");
      return;
    }
    navigate(`/podcasts?q=${encodeURIComponent(q)}`);
  };

  const totalPages = Math.max(1, Number(detail.totalPages) || 1);
  const pageNumbers = React.useMemo(() => {
    const radius = 2;
    const start = Math.max(0, page - radius);
    const end = Math.min(totalPages - 1, page + radius);
    const result = [];
    for (let i = start; i <= end; i += 1) result.push(i);
    return result;
  }, [page, totalPages]);

  const title = safeText(detail.title, "Подкаст");
  const description = safeText(detail.description, "Подкаст специалиста Бюро Души.");

  return (
    <div className="podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>{`${title} | Подкасты Бюро Души`}</title>
        <meta name="description" content={description} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="podcasts"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <form className="podcasts-topbar__search" onSubmit={onSearchSubmit}>
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="8.5" cy="8.5" r="5.5" />
                  <path d="M13 13l4 4" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="Найти подкаст, тему, психолога..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </form>
            </div>
            <div className="podcasts-topbar__right">
              {canManagePodcasts ? (
                <button className="podcasts-btn podcasts-btn--mint" type="button" onClick={() => navigate("/dashboard/podcasts")}>
                  Мои подкасты
                </button>
              ) : null}
              <button className="podcasts-btn podcasts-btn--mint" type="button" onClick={() => navigate("/podcasts")}>
                ← К каталогу
              </button>
              <div className="podcasts-topbar__separator" />
              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>
                {initials(me?.name || me?.login)}
              </button>
            </div>
          </header>

          <main className="podcasts-content">
            {loading ? <div className="podcasts-empty">Загрузка подкаста…</div> : null}
            {!loading && error ? <div className="podcasts-empty">{error}</div> : null}

            {!loading && !error ? (
              <>
                <div className="podcast-detail-crumbs">
                  <Link to="/podcasts">Подкасты</Link>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 5l5 5-5 5" />
                  </svg>
                  <span>{title}</span>
                </div>

                <section className="podcast-detail-hero">
                  <div className="podcast-detail-cover">
                    {detail.coverImageUrl ? (
                      <img src={detail.coverImageUrl} alt={title} />
                    ) : (
                      <div className="podcast-detail-cover-fallback">{detail.coverEmoji || "🎙️"}</div>
                    )}
                  </div>

                  <div className="podcast-detail-info">
                    <div className={`podcast-detail-cat ${topicColorClass(detail.topics[0], 0)}`}>
                      {detail.topics.length ? detail.topics.join(" · ") : "Подкаст"}
                    </div>
                    <h1 className="podcast-detail-title">{title}</h1>
                    <p className="podcast-detail-desc">{description}</p>

                    <div className="podcast-detail-author">
                      {detail.authorUserId ? (
                        <button
                          type="button"
                          className="b-psych-link-btn"
                          onClick={(event) => openPsychologistProfile(event, detail.authorUserId)}
                        >
                          <div className="podcast-detail-author-pic">{initials(detail.authorName)}</div>
                          <div className="podcast-detail-author-name">
                            {detail.authorName}
                            {detail.episodesCount ? ` · ${formatEpisodeCount(detail.episodesCount)}` : ""}
                          </div>
                        </button>
                      ) : (
                        <>
                          <div className="podcast-detail-author-pic">{initials(detail.authorName)}</div>
                          <div className="podcast-detail-author-name">
                            {detail.authorName}
                            {detail.episodesCount ? ` · ${formatEpisodeCount(detail.episodesCount)}` : ""}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="podcast-detail-actions">
                      <button
                        type="button"
                        className="podcasts-btn podcasts-btn--play"
                        onClick={() => playEpisodeInline(detail.lastEpisode || sortedEpisodes[0])}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M5 3l9 5-9 5V3z" />
                        </svg>
                        Слушать последний
                      </button>

                      <button
                        type="button"
                        className={`podcasts-btn podcasts-btn--subtle podcast-detail-sub-btn ${detail.subscribed ? "subscribed" : ""}`}
                        onClick={toggleSubscribe}
                      >
                        {detail.subscribed ? "✓ Подписан" : "Подписаться"}
                      </button>

                      <button
                        type="button"
                        className="podcasts-btn podcasts-btn--subtle"
                        onClick={() => {
                          navigator.clipboard?.writeText(window.location.href).then(
                            () => toast.success("Ссылка скопирована"),
                            () => toast.info("Скопируйте ссылку из адресной строки")
                          );
                        }}
                      >
                        Поделиться
                      </button>

                      <ReportContentButton
                        targetType="PODCAST"
                        targetId={detail.id}
                        title={title}
                        contentSnippet={description}
                        className="podcasts-btn podcasts-btn--subtle podcasts-btn--report"
                      />
                    </div>
                  </div>
                </section>

                <section className="episodes-section podcast-detail-episodes">
                  <div className="episodes-header">
                    <div className="podcasts-section-label">Все эпизоды</div>
                    <select
                      className="podcast-detail-sort"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                    >
                      <option value="new">Сначала новые</option>
                      <option value="old">Сначала старые</option>
                    </select>
                  </div>

                  <div className="episodes-list">
                    {sortedEpisodes.length === 0 ? (
                      <div className="podcasts-empty podcasts-empty--inline">Эпизоды пока не опубликованы</div>
                    ) : (
                      sortedEpisodes.map((episode, index) => (
                        <article
                          className="episode-item"
                          key={episode.id || `ep-${index}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => openEpisode(episode)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openEpisode(episode);
                            }
                          }}
                        >
                          <div className="episode-num">{episode.episodeNumber || index + 1}</div>
                          <div className="episode-thumb">
                            {episode.coverImageUrl ? (
                              <img src={episode.coverImageUrl} alt={episode.title} />
                            ) : (
                              <span>{episode.podcastCoverEmoji || detail.coverEmoji || "🎙️"}</span>
                            )}
                          </div>
                          <div className="episode-info">
                            <div className="episode-title">{episode.title}</div>
                            <div className="episode-meta">
                              <span>{episode.publishedLabel}</span>
                              <span className="episode-dot" />
                              <span>{episode.durationLabel}</span>
                              {episode.isNew && page === 0 && index === 0 ? (
                                <>
                                  <span className="episode-dot" />
                                  <span className="podcast-detail-episode-badge">Новый</span>
                                </>
                              ) : null}
                            </div>
                          </div>
                          <div className="episode-actions">
                            <button
                              type="button"
                              className={`ep-icon-btn ${episode.saved ? "saved" : ""}`}
                              onClick={(event) => toggleSaveEpisode(event, episode)}
                              title={episode.saved ? "Убрать из сохранённого" : "Сохранить"}
                            >
                              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 2h10a1 1 0 0 1 1 1v15l-6-4-6 4V3a1 1 0 0 1 1-1z" />
                              </svg>
                            </button>
                            <div className="ep-duration">{episode.durationLabel}</div>
                            <button
                              type="button"
                              className="ep-play-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                playEpisodeInline(episode);
                              }}
                              title="Слушать"
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="#fff">
                                <path d="M5 3l9 5-9 5V3z" />
                              </svg>
                            </button>
                          </div>
                        </article>
                      ))
                    )}
                  </div>
                </section>

                {totalPages > 1 ? (
                  <div className="pagination">
                    <button
                      type="button"
                      className="page-btn"
                      onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                      disabled={page <= 0}
                    >
                      ←
                    </button>
                    {pageNumbers.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`page-btn ${pageNumber === page ? "active" : ""}`}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="page-btn"
                      onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      →
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
